import type {FC} from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import {
  type ReportType,
  SELECTION_PHASE_IDS,
} from '../../../../../core/entities/reportAnalytics/schema';
import {convertStringToReportType} from '../../../../../core/factories/reports/convertStringToReportType';
import {useGetDepartmentsInSummary} from '../../../../../core/repositories/reportSummaryRepository';
import {ReportTypeLabel} from '../../../../reports/components/Container/Presenter/ReportTypeLabel';
import {
  AiTodoGenerateModal,
  type DepartmentOption,
} from '../Presenter/Progress/AiTodoGenerateModal';
import {DepartmentSelectItem} from '../Presenter/Progress/DepartmentSelectItem';

/**
 * 「指定しない」オプションの値
 */
const NO_SPECIFICATION_VALUE = 'none';

type Props = {
  opened: boolean;
  onClose: () => void;
  onGenerate: (params: {
    departmentID?: number;
    selectionPhaseID?: number;
  }) => void;
  isGenerating: boolean;
  companyID: number | null;
  companyName?: string;
  stepName?: string;
};

/**
 * 「指定しない」オプション
 */
const NO_SPECIFICATION_OPTION: DepartmentOption = {
  value: NO_SPECIFICATION_VALUE,
  label: '部門・職種を指定しない',
  reportType: undefined,
};

/**
 * 部門データから選択オプションを生成するヘルパー関数
 * @param departments - 部門データの配列
 * @param reportType - 体験記タイプ
 * @returns 選択肢オプションの配列
 */
const createDepartmentOptions = (
  departments: Array<{departmentID: number; departmentName: string}>,
  reportType: ReportType,
): DepartmentOption[] => {
  return departments.map((dept) => ({
    value: `${dept.departmentID}-${reportType}`,
    label: dept.departmentName,
    reportType,
  }));
};

/**
 * 選択された値からAPIパラメータを生成するヘルパー関数
 * @param selectedOption - 選択された値（形式: "{departmentID}-{reportType}"）
 * @returns APIパラメータ（departmentIDとselectionPhaseID）
 */
const parseSelectedOptionToParams = (
  selectedOption: string,
): {departmentID?: number; selectionPhaseID?: number} => {
  if (!selectedOption || selectedOption === NO_SPECIFICATION_VALUE) {
    return {};
  }

  // "1-selection" -> departmentID: 1, reportType: "selection"
  const [deptIdStr, reportType] = selectedOption.split('-');
  const departmentID = Number.isNaN(deptIdStr) ? undefined : Number(deptIdStr);

  return {
    departmentID,
    selectionPhaseID:
      reportType === 'intern'
        ? SELECTION_PHASE_IDS.INTERN
        : SELECTION_PHASE_IDS.SELECTION,
  };
};

export const AiTodoGenerateModalContainer: FC<Props> = ({
  opened,
  onClose,
  onGenerate,
  isGenerating,
  companyID,
  companyName,
  stepName,
}) => {
  const [selectedOption, setSelectedOption] = useState('');
  // 部門データが0件の場合に自動生成を一度だけ実行するためのフラグ
  const hasAutoTriggeredRef = useRef(false);

  // 部門データ取得のクエリ有効化条件
  // モーダルが開いており、かつ有効なcompanyIDが存在する場合のみクエリを実行
  const isDepartmentQueryEnabled =
    opened && companyID !== null && companyID > 0;

  // 部門データ取得（selection）
  const {data: selectionDepts, isLoading: isLoadingSelection} =
    useGetDepartmentsInSummary({
      companyID: companyID ?? 0,
      queryParams: {reportType: 'selection'},
      config: {enabled: isDepartmentQueryEnabled},
    });

  // 部門データ取得（intern）
  const {data: internDepts, isLoading: isLoadingIntern} =
    useGetDepartmentsInSummary({
      companyID: companyID ?? 0,
      queryParams: {reportType: 'intern'},
      config: {enabled: isDepartmentQueryEnabled},
    });

  // 部門データのローディング状態
  // クエリが無効な場合はローディング状態もfalseとする
  // 注意: React Query v4では enabled: false でも isLoading が true になることがあるため条件分岐が必要
  const isDepartmentsLoading = isDepartmentQueryEnabled
    ? isLoadingSelection || isLoadingIntern
    : false;

  // 選択肢のフォーマット
  const departmentOptions = useMemo(() => {
    const options: DepartmentOption[] = [];

    if (selectionDepts?.ok && selectionDepts.value.departments) {
      options.push(
        ...createDepartmentOptions(
          selectionDepts.value.departments,
          'selection',
        ),
      );
    }

    if (internDepts?.ok && internDepts.value.departments) {
      options.push(
        ...createDepartmentOptions(internDepts.value.departments, 'intern'),
      );
    }

    // 部門データがある場合は「指定しない」オプションを末尾に追加
    if (options.length > 0) {
      options.push(NO_SPECIFICATION_OPTION);
    }

    return options;
  }, [selectionDepts, internDepts]);

  // 選択した部門のラベル取得
  const selectedDepartmentLabel = useMemo(() => {
    if (!selectedOption || selectedOption === NO_SPECIFICATION_VALUE) return '';
    const option = departmentOptions.find((o) => o.value === selectedOption);
    if (!option) return '';
    const typeLabel = option.reportType === 'intern' ? 'インターン' : '本選考';
    return `${option.label}（${typeLabel}）`;
  }, [selectedOption, departmentOptions]);

  // 部門データが0件の場合、自動的にパラメータ未指定で生成を開始
  useEffect(() => {
    if (
      opened &&
      !isDepartmentsLoading &&
      departmentOptions.length === 0 &&
      !isGenerating &&
      !hasAutoTriggeredRef.current
    ) {
      hasAutoTriggeredRef.current = true;
      // departmentIDとselectionPhaseIDを未指定でリクエスト
      onGenerate({});
    }
  }, [
    opened,
    isDepartmentsLoading,
    departmentOptions.length,
    isGenerating,
    onGenerate,
  ]);

  // モーダルが開かれたら選択状態をリセット
  useEffect(() => {
    if (opened) {
      setSelectedOption('');
    } else {
      hasAutoTriggeredRef.current = false;
    }
  }, [opened]);

  const handleSubmit = () => {
    const params = parseSelectedOptionToParams(selectedOption);
    onGenerate(params);
  };

  const handleSelectChange = (value: string | null) => {
    setSelectedOption(value ?? '');
  };

  const handleClose = () => {
    onClose();
    setSelectedOption('');
  };

  // leftSection用のバッジを作成（「指定しない」の場合は表示しない）
  const leftSectionBadge =
    selectedOption && selectedOption !== NO_SPECIFICATION_VALUE ? (
      <ReportTypeLabel
        type={convertStringToReportType(
          selectedOption.split('-')[1] as ReportType,
        )}
      />
    ) : null;

  return (
    <AiTodoGenerateModal
      opened={opened}
      onClose={handleClose}
      onSubmit={handleSubmit}
      departmentOptions={departmentOptions}
      selectedOption={selectedOption}
      onSelectChange={handleSelectChange}
      isLoading={isGenerating}
      itemComponent={DepartmentSelectItem}
      badgeComponent={leftSectionBadge}
      companyName={companyName}
      selectedDepartmentLabel={selectedDepartmentLabel}
      stepName={stepName}
    />
  );
};
